import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Edit, Wrench, Clock, TrendingUp, DollarSign, FileText, Calendar, Eye, Download, ChevronLeft, ChevronRight, Grid3x3, Plus, Edit as EditIcon, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { apiService } from '../services/api';
import { EquipmentDto, RepairVsReplaceAnalysisDto, WorkOrderDto, MaintenanceScheduleDto } from '../types';
import { formatCurrency, formatDate, getEquipmentPhotoUrl } from '../utils/formatters';
import { Link } from 'react-router-dom';
import Modal from '../components/ui/Modal';
import ImageThumbnail from '../components/common/ImageThumbnail';

const EquipmentDetailPage: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const equipmentId = parseInt(id!);
	const [activeTab, setActiveTab] = useState<'details' | 'history' | 'analysis' | 'pm-schedules'>('details');
	const [imageModalOpen, setImageModalOpen] = useState(false);
	const [galleryModalOpen, setGalleryModalOpen] = useState(false);
	const [currentImageUrl, setCurrentImageUrl] = useState<string>('');
	const [currentImageName, setCurrentImageName] = useState<string>('');
	const [currentImageDescription, setCurrentImageDescription] = useState<string>('');
	const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
	const [galleryImageUrl, setGalleryImageUrl] = useState<string>('');
	const [galleryImageLoading, setGalleryImageLoading] = useState<boolean>(false);
	const galleryImageUrlRef = useRef<string>('');
	const MAX_PREVIEW_PHOTOS = 6;

	// Fetch equipment details
	const {
		data: equipmentResponse,
		isLoading: equipmentLoading,
		error: equipmentError
	} = useQuery({
		queryKey: ['equipment', equipmentId],
		queryFn: () => apiService.getEquipmentById(equipmentId),
		enabled: !!equipmentId
	});

	// Fetch work orders for this equipment
	const {
		data: workOrdersResponse,
		isLoading: workOrdersLoading
	} = useQuery({
		queryKey: ['equipment-work-orders', equipmentId],
		queryFn: () => apiService.getEquipmentWorkOrders(equipmentId),
		enabled: !!equipmentId
	});

	// Fetch cost analysis
	const {
		data: analysisResponse,
		isLoading: analysisLoading
	} = useQuery({
		queryKey: ['equipment-analysis', equipmentId],
		queryFn: () => apiService.getEquipmentCostAnalysis(equipmentId),
		enabled: !!equipmentId
	});

	// Fetch PM schedules for this equipment
	const {
		data: schedulesResponse,
		isLoading: schedulesLoading
	} = useQuery({
		queryKey: ['maintenance-schedules', equipmentId],
		queryFn: () => apiService.getMaintenanceSchedules(equipmentId),
		enabled: !!equipmentId
	});

	// Image handlers - must be defined before any early returns
	const handleViewImage = useCallback(async (filePath: string, fileName: string, description?: string) => {
		try {
			const blob = await apiService.downloadEquipmentPhoto(filePath);
			const url = window.URL.createObjectURL(blob);
			setCurrentImageUrl(url);
			setCurrentImageName(fileName);
			setCurrentImageDescription(description || '');
			setImageModalOpen(true);
		} catch (error) {
			toast.error('Failed to load image');
			console.error('Error loading image:', error);
		}
	}, []);

	const handleDownloadImage = useCallback(async (filePath: string, fileName: string) => {
		try {
			const blob = await apiService.downloadEquipmentPhoto(filePath);
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = fileName;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (error) {
			toast.error('Failed to download file');
			console.error('Error downloading file:', error);
		}
	}, []);

	const closeImageModal = useCallback(() => {
		setImageModalOpen(false);
		if (currentImageUrl && currentImageUrl.startsWith('blob:')) {
			window.URL.revokeObjectURL(currentImageUrl);
		}
		setCurrentImageUrl('');
		setCurrentImageName('');
		setCurrentImageDescription('');
	}, [currentImageUrl]);

	const openGallery = useCallback((startIndex: number = 0) => {
		setCurrentImageIndex(startIndex);
		setGalleryModalOpen(true);
	}, []);

	const closeGallery = useCallback(() => {
		setGalleryModalOpen(false);
		setCurrentImageIndex(0);
		if (galleryImageUrlRef.current && galleryImageUrlRef.current.startsWith('blob:')) {
			window.URL.revokeObjectURL(galleryImageUrlRef.current);
			galleryImageUrlRef.current = '';
		}
		setGalleryImageUrl('');
	}, []);

	const navigateGallery = useCallback((direction: 'prev' | 'next', photos: any[]) => {
		if (direction === 'prev') {
			setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : photos.length - 1));
		} else {
			setCurrentImageIndex((prev) => (prev < photos.length - 1 ? prev + 1 : 0));
		}
	}, []);

	// Load gallery image when index changes
	useEffect(() => {
		const photos = equipmentResponse?.data?.photos;
		if (galleryModalOpen && photos && photos[currentImageIndex]) {
			const currentPhoto = photos[currentImageIndex];
			setGalleryImageLoading(true);
			
			// Clean up previous URL
			const previousUrl = galleryImageUrlRef.current;
			
			apiService.downloadEquipmentPhoto(currentPhoto.filePath)
				.then(blob => {
					const url = window.URL.createObjectURL(blob);
					if (previousUrl && previousUrl.startsWith('blob:')) {
						window.URL.revokeObjectURL(previousUrl);
					}
					galleryImageUrlRef.current = url;
					setGalleryImageUrl(url);
					setGalleryImageLoading(false);
				})
				.catch(() => {
					setGalleryImageLoading(false);
					toast.error('Failed to load image');
				});
		}
		
		return () => {
			if (galleryImageUrlRef.current && galleryImageUrlRef.current.startsWith('blob:')) {
				window.URL.revokeObjectURL(galleryImageUrlRef.current);
				galleryImageUrlRef.current = '';
			}
		};
	}, [galleryModalOpen, currentImageIndex, equipmentResponse?.data?.photos]);

	if (equipmentLoading) {
		return (
			<div className="p-4">
				<div className="animate-pulse">
					<div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-3"></div>
					<div className="h-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
				</div>
			</div>
		);
	}

	if (equipmentError || !equipmentResponse?.data) {
		return (
			<div className="p-4">
				<div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
					<p className="text-red-800 dark:text-red-200">Error loading equipment details. Please try again.</p>
				</div>
			</div>
		);
	}

	const equipment = equipmentResponse.data;
	const workOrders = workOrdersResponse?.data || [];
	const analysis = analysisResponse?.data;

	const getStatusColor = (status: number): string => {
		const colorMap: Record<number, string> = {
			0: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
			1: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
			2: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
		};
		return colorMap[status] || 'bg-gray-100 text-gray-800';
	};

	const getRecommendationColor = (recommendation: string): string => {
		if (recommendation === 'replace') return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
		if (recommendation === 'monitor') return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
		return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
	};

	const getCostStatusColor = (status: string): string => {
		if (status === 'replace') return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
		if (status === 'monitor') return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
		return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
	};

	const getTrendColor = (trend: string): string => {
		if (trend === 'increasing') return 'text-red-600 dark:text-red-400';
		if (trend === 'decreasing') return 'text-green-600 dark:text-green-400';
		return 'text-gray-600 dark:text-gray-400';
	};

	return (
		<div className="p-4">
			{/* Header */}
			<div className="mb-2">
				<div className="flex items-center justify-between mb-2">
					<div>
						<div className="flex items-center gap-2">
							<span className="text-2xl">{equipment.equipmentTypeIcon}</span>
							<h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{equipment.name}</h1>
						</div>
						<p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
							{equipment.equipmentTypeDisplay} • {equipment.clientLocationName}
						</p>
					</div>
					<button
						onClick={() => navigate(`/equipment/${equipment.id}/edit`)}
						className="flex items-center gap-2 px-4 py-2 bg-accent-color text-white rounded-lg hover:bg-accent-color/90 transition-colors"
					>
						<Edit className="w-4 h-4" />
						Edit Equipment
					</button>
				</div>
				{/* Back to Equipment Link */}
				<button
					onClick={() => navigate('/equipment')}
					className="flex items-center gap-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
				>
					<ArrowLeft className="w-4 h-4" />
					<span>Back to Equipment</span>
				</button>
			</div>

			{/* Tabs */}
			<div className="border-b border-gray-200 dark:border-gray-700">
				<nav className="flex space-x-8">
					<button
						onClick={() => setActiveTab('details')}
						className={`py-3 px-1 border-b-2 font-medium text-sm ${
							activeTab === 'details'
								? 'border-accent-color text-accent-color'
								: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
						}`}
					>
						Details
					</button>
					<button
						onClick={() => setActiveTab('history')}
						className={`py-3 px-1 border-b-2 font-medium text-sm ${
							activeTab === 'history'
								? 'border-accent-color text-accent-color'
								: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
						}`}
					>
						Service History ({workOrders.length})
					</button>
					<button
						onClick={() => setActiveTab('analysis')}
						className={`py-3 px-1 border-b-2 font-medium text-sm ${
							activeTab === 'analysis'
								? 'border-accent-color text-accent-color'
								: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
						}`}
					>
						Repair vs Replace
					</button>
					<button
						onClick={() => setActiveTab('pm-schedules')}
						className={`py-3 px-1 border-b-2 font-medium text-sm ${
							activeTab === 'pm-schedules'
								? 'border-accent-color text-accent-color'
								: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
						}`}
					>
						PM Schedules
					</button>
				</nav>
			</div>

			{/* Tab Content */}
			<div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
				{activeTab === 'details' && (
					<div className="p-4 space-y-4">
						{/* Status Badge */}
						<div className="flex items-center gap-2">
							<span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(equipment.status)}`}>
								{equipment.statusDisplay}
							</span>
						</div>

						{/* Basic Information */}
						<div>
							<h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Basic Information</h2>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div>
									<label className="text-sm font-medium text-gray-500 dark:text-gray-400">Equipment Type</label>
									<p className="mt-1 text-gray-900 dark:text-gray-100">{equipment.equipmentTypeDisplay}</p>
								</div>
								<div>
									<label className="text-sm font-medium text-gray-500 dark:text-gray-400">Location</label>
									<p className="mt-1 text-gray-900 dark:text-gray-100">{equipment.clientLocationName}</p>
									{equipment.clientName && (
										<p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{equipment.clientName}</p>
									)}
								</div>
								{equipment.make && (
									<div>
										<label className="text-sm font-medium text-gray-500 dark:text-gray-400">Make</label>
										<p className="mt-1 text-gray-900 dark:text-gray-100">{equipment.make}</p>
									</div>
								)}
								{equipment.model && (
									<div>
										<label className="text-sm font-medium text-gray-500 dark:text-gray-400">Model</label>
										<p className="mt-1 text-gray-900 dark:text-gray-100">{equipment.model}</p>
									</div>
								)}
								{equipment.serialNumber && (
									<div>
										<label className="text-sm font-medium text-gray-500 dark:text-gray-400">Serial Number</label>
										<p className="mt-1 text-gray-900 dark:text-gray-100">{equipment.serialNumber}</p>
									</div>
								)}
								{equipment.installDate && (
									<div>
										<label className="text-sm font-medium text-gray-500 dark:text-gray-400">Install Date</label>
										<p className="mt-1 text-gray-900 dark:text-gray-100">{formatDate(equipment.installDate)}</p>
									</div>
								)}
								{equipment.expectedLifespanYears && (
									<div>
										<label className="text-sm font-medium text-gray-500 dark:text-gray-400">Expected Lifespan</label>
										<p className="mt-1 text-gray-900 dark:text-gray-100">{equipment.expectedLifespanYears} years</p>
									</div>
								)}
								{equipment.replacementCost && (
									<div>
										<label className="text-sm font-medium text-gray-500 dark:text-gray-400">Replacement Cost</label>
										<p className="mt-1 text-gray-900 dark:text-gray-100">{formatCurrency(equipment.replacementCost)}</p>
									</div>
								)}
							</div>
						</div>

						{/* Warranty Information */}
						{(equipment.warrantyExpirationDate || equipment.warrantyProvider) && (
							<div>
								<h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Warranty Information</h2>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									{equipment.warrantyProvider && (
										<div>
											<label className="text-sm font-medium text-gray-500 dark:text-gray-400">Warranty Provider</label>
											<p className="mt-1 text-gray-900 dark:text-gray-100">{equipment.warrantyProvider}</p>
										</div>
									)}
									{equipment.warrantyExpirationDate && (
										<div>
											<label className="text-sm font-medium text-gray-500 dark:text-gray-400">Warranty Expiration</label>
											<p className="mt-1 text-gray-900 dark:text-gray-100">{formatDate(equipment.warrantyExpirationDate)}</p>
										</div>
									)}
								</div>
							</div>
						)}

						{/* Notes */}
						{equipment.notes && (
							<div>
								<h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Notes</h2>
								<p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{equipment.notes}</p>
							</div>
						)}

						{/* Photos */}
						{equipment.photos && equipment.photos.length > 0 && (
							<div>
								<div className="flex items-center justify-between mb-4">
									<h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Photos</h2>
									{equipment.photos.length > MAX_PREVIEW_PHOTOS && (
										<button
											type="button"
											onClick={() => openGallery(0)}
											className="flex items-center gap-2 px-3 py-1.5 text-sm text-accent-color hover:bg-accent-color/10 dark:hover:bg-accent-color/20 rounded-md transition-colors"
										>
											<Grid3x3 className="w-4 h-4" />
											View All ({equipment.photos.length})
										</button>
									)}
								</div>
								<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
									{equipment.photos.slice(0, MAX_PREVIEW_PHOTOS).map((photo, index) => (
										<div key={photo.id} className="space-y-2">
											<div 
												className="relative group cursor-pointer rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 hover:border-accent-color dark:hover:border-accent-color transition-colors"
												onClick={() => openGallery(index)}
											>
												<div className="aspect-video bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
													<ImageThumbnail
														filePath={photo.filePath}
														fileName={photo.fileName}
														downloadFn={(path: string) => apiService.downloadEquipmentPhoto(path)}
														loadImmediately={true}
													/>
												</div>
												{/* Action Buttons - Show on hover */}
												<div className="absolute top-2 right-2 flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
													<button
														type="button"
														onClick={(e) => {
															e.stopPropagation();
															handleViewImage(photo.filePath, photo.fileName, photo.description);
														}}
														className="p-1.5 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-md hover:bg-white dark:hover:bg-gray-800 transition-colors shadow-sm"
														title="View"
													>
														<Eye className="h-3.5 w-3.5 text-gray-700 dark:text-gray-300" />
													</button>
													<button
														type="button"
														onClick={(e) => {
															e.stopPropagation();
															handleDownloadImage(photo.filePath, photo.fileName);
														}}
														className="p-1.5 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-md hover:bg-white dark:hover:bg-gray-800 transition-colors shadow-sm"
														title="Download"
													>
														<Download className="h-3.5 w-3.5 text-gray-700 dark:text-gray-300" />
													</button>
												</div>
											</div>
											{/* Description */}
											{photo.description && (
												<p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
													{photo.description}
												</p>
											)}
										</div>
									))}
								</div>
							</div>
						)}

						{/* Links */}
						{(equipment.photoUrl || equipment.manualUrl) && (
							<div>
								<h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Resources</h2>
								<div className="flex flex-wrap gap-4">
									{equipment.photoUrl && (
										<a
											href={getEquipmentPhotoUrl(equipment.photoUrl)}
											target="_blank"
											rel="noopener noreferrer"
											className="flex items-center gap-2 text-accent-color hover:underline"
										>
											<FileText className="w-4 h-4" />
											View Photo
										</a>
									)}
									{equipment.manualUrl && (
										<a
											href={equipment.manualUrl}
											target="_blank"
											rel="noopener noreferrer"
											className="flex items-center gap-2 text-accent-color hover:underline"
										>
											<FileText className="w-4 h-4" />
											View Manual
										</a>
									)}
								</div>
							</div>
						)}

						{/* Statistics */}
						<div>
							<h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Statistics</h2>
							<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
								<div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
									<label className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Work Orders</label>
									<p className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">{equipment.workOrderCount}</p>
								</div>
								<div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
									<label className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Maintenance Cost</label>
									<p className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">
										{formatCurrency(equipment.totalMaintenanceCost)}
									</p>
								</div>
								<div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
									<label className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Service</label>
									<p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">
										{equipment.lastServiceDate ? formatDate(equipment.lastServiceDate) : 'Never'}
									</p>
								</div>
							</div>
						</div>
					</div>
				)}

				{activeTab === 'history' && (
					<div className="p-4">
						{workOrdersLoading ? (
							<div className="animate-pulse">
								<div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
							</div>
						) : workOrders.length === 0 ? (
							<div className="text-center py-8">
								<Wrench className="w-12 h-12 text-gray-400 mx-auto mb-3" />
								<p className="text-gray-500 dark:text-gray-400">No work orders found for this equipment.</p>
							</div>
						) : (
							<div className="space-y-4">
								{workOrders.map((wo: WorkOrderDto) => (
									<Link
										key={wo.id}
										to={`/workorders/${wo.id}/contact`}
										className="block p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
									>
										<div className="flex items-center justify-between">
											<div className="flex-1">
												<div className="flex items-center gap-2 mb-2">
													<span className="font-medium text-gray-900 dark:text-gray-100">{wo.workOrderNumberFormatted}</span>
													<span className={`px-2 py-1 rounded-full text-xs font-medium ${
														wo.type === 1 ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' : ''
													}`}>
														{wo.typeDisplay}
													</span>
												</div>
												<p className="text-sm text-gray-600 dark:text-gray-400">{wo.description || 'No description'}</p>
												<div className="flex items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
													<span>{formatDate(wo.createdDate)}</span>
													{wo.nteAmount && <span>NTE: {formatCurrency(wo.nteAmount)}</span>}
												</div>
											</div>
											<div className="text-right">
												<span className={`px-2 py-1 rounded-full text-xs font-medium ${
													wo.statusName === 'Completed' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
												}`}>
													{wo.statusName}
												</span>
											</div>
										</div>
									</Link>
								))}
							</div>
						)}
					</div>
				)}

				{activeTab === 'analysis' && (
					<div className="p-4">
						{analysisLoading ? (
							<div className="animate-pulse">
								<div className="h-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
							</div>
						) : !analysis ? (
							<div className="text-center py-8">
								<TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-3" />
								<p className="text-gray-500 dark:text-gray-400">No analysis data available.</p>
							</div>
						) : (
							<div className="space-y-4">
								{/* Recommendation */}
								<div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
									<div className="flex items-center justify-between mb-3">
										<h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Recommendation</h3>
										<span className={`px-3 py-1 rounded-full text-sm font-medium ${getRecommendationColor(analysis.recommendation)}`}>
											{analysis.recommendation.charAt(0).toUpperCase() + analysis.recommendation.slice(1)}
										</span>
									</div>
									<p className="text-gray-700 dark:text-gray-300">{analysis.recommendationReason}</p>
								</div>

								{/* Cost Analysis */}
								<div>
									<h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Cost Analysis</h3>
									<div className="space-y-4">
										{/* Cost Gauge - Visual Progress Bar */}
										<div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
											<div className="flex items-center justify-between mb-2">
												<label className="text-sm font-medium text-gray-700 dark:text-gray-300">Maintenance Cost vs Replacement Cost</label>
												<span className={`px-3 py-1 rounded-full text-sm font-medium ${getCostStatusColor(analysis.costStatus)}`}>
													{analysis.costStatus.charAt(0).toUpperCase() + analysis.costStatus.slice(1)}
												</span>
											</div>
											<div className="mt-4">
												<div className="flex items-center justify-between mb-2">
													<span className="text-sm text-gray-600 dark:text-gray-400">
														{formatCurrency(analysis.totalMaintenanceCost)} / {formatCurrency(analysis.replacementCost)}
													</span>
													<span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
														{analysis.costPercentage.toFixed(1)}%
													</span>
												</div>
												<div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-8 relative overflow-hidden">
													{/* Color zones */}
													<div className="absolute inset-0 flex">
														<div className="h-full bg-green-500" style={{ width: '30%' }}></div>
														<div className="h-full bg-yellow-500" style={{ width: '40%' }}></div>
														<div className="h-full bg-red-500" style={{ width: '30%' }}></div>
													</div>
													{/* Progress bar */}
													<div
														className={`h-full rounded-full transition-all duration-500 ${
															analysis.costPercentage < 30
																? 'bg-green-600 dark:bg-green-500'
																: analysis.costPercentage < 70
																? 'bg-yellow-600 dark:bg-yellow-500'
																: 'bg-red-600 dark:bg-red-500'
														}`}
														style={{ width: `${Math.min(analysis.costPercentage, 100)}%` }}
													></div>
													{/* Percentage text overlay */}
													<div className="absolute inset-0 flex items-center justify-center">
														<span className="text-xs font-semibold text-white drop-shadow-md">
															{analysis.costPercentage.toFixed(1)}%
														</span>
													</div>
												</div>
												<div className="flex items-center justify-between mt-2 text-xs text-gray-500 dark:text-gray-400">
													<span>🟢 Good (0-30%)</span>
													<span>🟡 Monitor (30-70%)</span>
													<span>🔴 Replace (70%+)</span>
												</div>
											</div>
										</div>

										<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
											<div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
												<label className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Maintenance Cost</label>
												<p className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">
													{formatCurrency(analysis.totalMaintenanceCost)}
												</p>
											</div>
											<div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
												<label className="text-sm font-medium text-gray-500 dark:text-gray-400">Replacement Cost</label>
												<p className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">
													{formatCurrency(analysis.replacementCost)}
												</p>
											</div>
											<div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
												<label className="text-sm font-medium text-gray-500 dark:text-gray-400">Cost Trend</label>
												<p className="mt-2">
													<span className={`text-sm font-medium ${getTrendColor(analysis.costTrend)}`}>
														{analysis.costTrend.charAt(0).toUpperCase() + analysis.costTrend.slice(1)}
													</span>
												</p>
												<div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
													Last 12 months: {formatCurrency(analysis.last12MonthsCost)}<br />
													Previous 12 months: {formatCurrency(analysis.previous12MonthsCost)}
												</div>
											</div>
										</div>
									</div>
								</div>

								{/* Age Analysis */}
								<div>
									<h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Age Analysis</h3>
									{analysis.expectedLifespanYears ? (
										<div className="space-y-4">
											{/* Age vs Lifespan Gauge - Visual Progress Bar */}
											<div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
												<div className="flex items-center justify-between mb-2">
													<label className="text-sm font-medium text-gray-700 dark:text-gray-300">Equipment Age vs Expected Lifespan</label>
													<span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
														{analysis.agePercentage.toFixed(1)}% of lifespan used
													</span>
												</div>
												<div className="mt-4">
													<div className="flex items-center justify-between mb-2">
														<span className="text-sm text-gray-600 dark:text-gray-400">
															{analysis.ageYears.toFixed(1)} years / {analysis.expectedLifespanYears} years
														</span>
														<span className={`text-sm font-semibold ${
															analysis.agePercentage < 50
																? 'text-green-600 dark:text-green-400'
																: analysis.agePercentage < 80
																? 'text-yellow-600 dark:text-yellow-400'
																: 'text-red-600 dark:text-red-400'
														}`}>
															{analysis.agePercentage < 50 ? '🟢 Early Life' : analysis.agePercentage < 80 ? '🟡 Mid Life' : '🔴 End of Life'}
														</span>
													</div>
													<div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-8 relative overflow-hidden">
														{/* Color zones */}
														<div className="absolute inset-0 flex">
															<div className="h-full bg-green-500" style={{ width: '50%' }}></div>
															<div className="h-full bg-yellow-500" style={{ width: '30%' }}></div>
															<div className="h-full bg-red-500" style={{ width: '20%' }}></div>
														</div>
														{/* Progress bar */}
														<div
															className={`h-full rounded-full transition-all duration-500 ${
																analysis.agePercentage < 50
																	? 'bg-green-600 dark:bg-green-500'
																	: analysis.agePercentage < 80
																	? 'bg-yellow-600 dark:bg-yellow-500'
																	: 'bg-red-600 dark:bg-red-500'
															}`}
															style={{ width: `${Math.min(analysis.agePercentage, 100)}%` }}
														></div>
														{/* Percentage text overlay */}
														<div className="absolute inset-0 flex items-center justify-center">
															<span className="text-xs font-semibold text-white drop-shadow-md">
																{analysis.agePercentage.toFixed(1)}%
															</span>
														</div>
													</div>
													<div className="flex items-center justify-between mt-2 text-xs text-gray-500 dark:text-gray-400">
														<span>🟢 Early Life (0-50%)</span>
														<span>🟡 Mid Life (50-80%)</span>
														<span>🔴 End of Life (80%+)</span>
													</div>
												</div>
											</div>

											<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
												<div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
													<label className="text-sm font-medium text-gray-500 dark:text-gray-400">Current Age</label>
													<p className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">
														{analysis.ageYears.toFixed(1)} years
													</p>
												</div>
												<div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
													<label className="text-sm font-medium text-gray-500 dark:text-gray-400">Expected Lifespan</label>
													<p className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">
														{analysis.expectedLifespanYears} years
													</p>
												</div>
											</div>
										</div>
									) : (
										<div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
											<label className="text-sm font-medium text-gray-500 dark:text-gray-400">Current Age</label>
											<p className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">
												{analysis.ageYears.toFixed(1)} years
											</p>
											<p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
												Expected lifespan not set. Add expected lifespan to see age analysis.
											</p>
										</div>
									)}
								</div>

								{/* Service Frequency */}
								<div>
									<h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Service Frequency</h3>
									<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
										<div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
											<label className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Work Orders</label>
											<p className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">{analysis.workOrderCount}</p>
										</div>
										{analysis.lastServiceDate && (
											<div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
												<label className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Service</label>
												<p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">
													{formatDate(analysis.lastServiceDate)}
												</p>
												{analysis.daysSinceLastService && (
													<p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
														{Math.round(analysis.daysSinceLastService)} days ago
													</p>
												)}
											</div>
										)}
										{analysis.averageDaysBetweenServices && (
											<div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
												<label className="text-sm font-medium text-gray-500 dark:text-gray-400">Avg. Days Between Services</label>
												<p className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">
													{Math.round(analysis.averageDaysBetweenServices)}
												</p>
											</div>
										)}
									</div>
								</div>
							</div>
						)}
					</div>
				)}

				{activeTab === 'pm-schedules' && (
					<div className="p-4">
						<div className="flex justify-between items-center mb-4">
							<h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Preventative Maintenance Schedules</h3>
							<button
								onClick={() => navigate(`/maintenance-schedules/create?equipmentId=${equipmentId}`)}
								className="flex items-center gap-2 px-4 py-2 bg-accent-color text-white rounded-lg hover:bg-accent-color/90 transition-colors"
							>
								<Plus className="w-4 h-4" />
								Add Schedule
							</button>
						</div>

						{schedulesLoading ? (
							<div className="animate-pulse">
								<div className="h-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
							</div>
						) : !schedulesResponse?.data || schedulesResponse.data.length === 0 ? (
							<div className="text-center py-8">
								<Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
								<p className="text-gray-500 dark:text-gray-400 mb-4">No maintenance schedules found for this equipment.</p>
								<button
									onClick={() => navigate(`/maintenance-schedules/create?equipmentId=${equipmentId}`)}
									className="px-4 py-2 bg-accent-color text-white rounded-lg hover:bg-accent-color/90 transition-colors"
								>
									Create First Schedule
								</button>
							</div>
						) : (
							<div className="space-y-4">
								{schedulesResponse.data.map((schedule: MaintenanceScheduleDto) => (
									<div
										key={schedule.id}
										className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
									>
										<div className="flex justify-between items-start">
											<div className="flex-1">
												<div className="flex items-center gap-3 mb-2">
													<h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{schedule.name}</h4>
													{!schedule.isActive && (
														<span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200">
															Inactive
														</span>
													)}
													{schedule.isActive && schedule.isOverdue && (
														<span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
															Overdue
														</span>
													)}
													{schedule.isActive && !schedule.isOverdue && schedule.daysUntilDue <= 7 && (
														<span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
															Due Soon
														</span>
													)}
												</div>
												{schedule.description && (
													<p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{schedule.description}</p>
												)}
												<div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
													<div>
														<label className="text-gray-500 dark:text-gray-400">Frequency</label>
														<p className="text-gray-900 dark:text-gray-100 font-medium">{schedule.frequencyDisplay}</p>
													</div>
													<div>
														<label className="text-gray-500 dark:text-gray-400">Next Due</label>
														<p className={`text-gray-900 dark:text-gray-100 font-medium ${schedule.isOverdue ? 'text-red-600 dark:text-red-400' : schedule.daysUntilDue <= 7 ? 'text-yellow-600 dark:text-yellow-400' : ''}`}>
															{formatDate(schedule.nextDueDate)}
														</p>
														<p className="text-xs text-gray-500 dark:text-gray-400">
															{schedule.isOverdue ? `${Math.abs(schedule.daysUntilDue)} days overdue` : `${schedule.daysUntilDue} days`}
														</p>
													</div>
													<div>
														<label className="text-gray-500 dark:text-gray-400">Auto-Generate</label>
														<p className="text-gray-900 dark:text-gray-100 font-medium">
															{schedule.autoGenerateWorkOrders ? 'Yes' : 'No'}
														</p>
													</div>
													<div>
														<label className="text-gray-500 dark:text-gray-400">Completed</label>
														<p className="text-gray-900 dark:text-gray-100 font-medium">{schedule.totalCompleted}</p>
													</div>
												</div>
											</div>
											<div className="flex items-center gap-2 ml-4">
												<button
													onClick={() => navigate(`/maintenance-schedules/${schedule.id}/edit`)}
													className="p-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
													title="Edit Schedule"
												>
													<EditIcon className="w-4 h-4" />
												</button>
											</div>
										</div>
									</div>
								))}
							</div>
						)}
					</div>
				)}
			</div>

			{/* Image Modal */}
			{imageModalOpen && currentImageUrl && (
				<Modal
					isOpen={imageModalOpen}
					onClose={closeImageModal}
					title={currentImageName}
					maxWidth="6xl"
				>
					<div className="space-y-4">
						<div className="flex justify-center items-center h-[calc(100vh-10rem)]">
							<img
								src={currentImageUrl}
								alt={currentImageName}
								className="max-w-full max-h-[calc(100vh-12rem)] object-contain rounded-lg shadow-lg"
							/>
						</div>
						{currentImageDescription && (
							<div className="text-sm text-gray-600 dark:text-gray-400 text-center">
								{currentImageDescription}
							</div>
						)}
					</div>
				</Modal>
			)}

			{/* Gallery Modal */}
			{galleryModalOpen && equipment.photos && equipment.photos.length > 0 && equipment.photos[currentImageIndex] && (
				<Modal
					isOpen={galleryModalOpen}
					onClose={closeGallery}
					title={`All Photos (${equipment.photos.length})`}
					maxWidth="7xl"
				>
					<div className="space-y-4">
						{/* Current Photo View */}
						<div className="relative">
							<div className="flex justify-center items-center bg-gray-100 dark:bg-gray-900 rounded-lg min-h-[400px]">
								{galleryImageLoading ? (
									<div className="animate-pulse text-gray-400">Loading...</div>
								) : galleryImageUrl ? (
									<img
										src={galleryImageUrl}
										alt={equipment.photos[currentImageIndex].fileName}
										className="max-w-full max-h-[60vh] object-contain rounded-lg"
									/>
								) : (
									<div className="text-gray-400">Failed to load image</div>
								)}
							</div>
							{/* Navigation Buttons */}
							{equipment.photos.length > 1 && (
								<>
									<button
										type="button"
										onClick={() => navigateGallery('prev', equipment.photos)}
										className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-full hover:bg-white dark:hover:bg-gray-800 shadow-lg transition-colors z-10"
										title="Previous"
									>
										<ChevronLeft className="w-6 h-6 text-gray-700 dark:text-gray-300" />
									</button>
									<button
										type="button"
										onClick={() => navigateGallery('next', equipment.photos)}
										className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-full hover:bg-white dark:hover:bg-gray-800 shadow-lg transition-colors z-10"
										title="Next"
									>
										<ChevronRight className="w-6 h-6 text-gray-700 dark:text-gray-300" />
									</button>
								</>
							)}
							{/* Photo Info */}
							<div className="mt-4 text-center">
								<p className="text-sm font-medium text-gray-900 dark:text-gray-100">
									{equipment.photos[currentImageIndex].fileName}
								</p>
								<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
									{currentImageIndex + 1} of {equipment.photos.length}
								</p>
								{equipment.photos[currentImageIndex].description && (
									<p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
										{equipment.photos[currentImageIndex].description}
									</p>
								)}
							</div>
						</div>

						{/* Thumbnail Grid */}
						<div className="border-t border-gray-200 dark:border-gray-700 pt-4">
							<h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">All Photos</h3>
							<div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 max-h-64 overflow-y-auto">
								{equipment.photos.map((photo, index) => (
									<button
										key={photo.id}
										type="button"
										onClick={() => setCurrentImageIndex(index)}
										className={`relative aspect-video rounded-lg overflow-hidden border-2 transition-all ${
											index === currentImageIndex
												? 'border-accent-color ring-2 ring-accent-color/50'
												: 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
										}`}
									>
										<ImageThumbnail
											filePath={photo.filePath}
											fileName={photo.fileName}
											downloadFn={(path: string) => apiService.downloadEquipmentPhoto(path)}
											loadImmediately={true}
										/>
									</button>
								))}
							</div>
						</div>
					</div>
				</Modal>
			)}
		</div>
	);
};

export default EquipmentDetailPage;

